import { createCrudApi } from '@core/api/createCrudApi';
import type { BaseEntity } from '@core/interfaces';
import axiosInstance from '@core/utils/axiosInstance';
import { VestmentCategory } from '../src/enum/vestmentCategory';
import { VestmentType } from '../src/enum/vestmentType';
import type { AlbSubtype } from './albSubtype';
import type { Collar } from './collar';
import type { StoleCut } from './stoleCut';

export interface Vestment extends BaseEntity {
  name: string;
  type: VestmentType;
  imagePath: string | null;
  thumbnailPath?: string | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
  collars: Collar[];
  stoleCuts: StoleCut[];
  albSubtypes: AlbSubtype[];
  isHidden: boolean;
  sortOrder?: number;
}

export interface CreateVestmentData {
  name: string;
  type: VestmentType;
  image: File;
}

export type UpdateVestmentData = Partial<CreateVestmentData>;

const vestmentApi = createCrudApi<
  Vestment,
  CreateVestmentData,
  UpdateVestmentData
>('vestment');

export const getAllVestments = vestmentApi.getAll;
export const getVestmentById = vestmentApi.getById;
export const deleteVestment = vestmentApi.delete;

export const reorderVestments = (category: VestmentCategory, ids: number[]) =>
  vestmentApi.reorder(ids, { category });

function buildVestmentFormData(data: UpdateVestmentData) {
  const formData = new FormData();
  if (data.name !== undefined) {
    formData.append('name', data.name);
  }
  if (data.type !== undefined) {
    formData.append('type', data.type);
  }
  if (data.image) {
    formData.append('image', data.image);
  }
  return formData;
}

export async function createVestment(
  data: CreateVestmentData
): Promise<Vestment> {
  const formData = buildVestmentFormData(data);
  const response = await axiosInstance.post('/vestment', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export async function updateVestment(
  id: number,
  data: UpdateVestmentData
): Promise<Vestment> {
  const formData = buildVestmentFormData(data);
  const response = await axiosInstance.post(`/vestment/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}
